import { map } from 'rxjs';

import { inject } from '@angular/core';
import { type ActivatedRouteSnapshot, CanActivateFn, type RouterStateSnapshot, Router } from '@angular/router';

import { PlatformAuthOrchestratorService } from '@core/platform-auth/platform-auth-orchestrator.service';
import { PlatformSessionService } from '@core/platform-auth/platform-session.service';

/**
 * Rotas da equipe da plataforma: exige sessão e aplica regras de papel + troca obrigatória de senha.
 */
export const platformAuthGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => {
  const authOrchestrator = inject(PlatformAuthOrchestratorService);
  const session = inject(PlatformSessionService);
  const router = inject(Router);
  const requiredRoles = route.data['platformRoles'] as ('Admin' | 'Editor')[] | undefined;
  const targetPath = state.url;
  const isAccessDeniedRoute = targetPath.startsWith('/admin/acesso-negado');
  const isChangePasswordRoute = targetPath.startsWith('/admin/conta/trocar-senha');

  const evaluateAccess = (): boolean | import('@angular/router').UrlTree => {
    if (!session.hasValidSession()) {
      authOrchestrator.redirectToLoginWithReturnUrl(targetPath);
      return false;
    }

    if (!isChangePasswordRoute && session.mustChangePassword)
      return router.createUrlTree(['/admin/conta/trocar-senha']);

    if (requiredRoles == null || requiredRoles.length === 0)
      return true;

    const role = session.platformRole;
    if (role != null && requiredRoles.includes(role as 'Admin' | 'Editor'))
      return true;

    if (isAccessDeniedRoute)
      return true;

    return router.createUrlTree(['/admin/acesso-negado']);
  };

  if (session.hasValidSession())
    return evaluateAccess();

  if (!session.hasPersistedSession()) {
    authOrchestrator.redirectToLoginWithReturnUrl(targetPath);
    return false;
  }

  return authOrchestrator.refreshSession().pipe(
    map((ok) => {
      if (ok)
        return evaluateAccess();

      authOrchestrator.redirectToLoginWithReturnUrl(targetPath);
      return false;
    }),
  );
};
